import { Navigate } from "react-router-dom"

// Authentication related pages
import Login from "../pages/Authentication/Login"
import Logout from "../pages/Authentication/Logout"
import Register from "../pages/Authentication/Register"
import ForgetPwd from "../pages/Authentication/ForgetPassword"

// Dashboard
import Dashboard from "../pages/Dashboard/index"

// Job Pages
import JobGrid from "../pages/JobPages/JobGrid/index"
import JobDetails from "../pages/JobPages/JobDetails"
import JobCategories from "../pages/JobPages/JobCategories"
import JobList from "../pages/JobPages/JobList"
import ApplyJobs from "../pages/JobPages/ApplyJobs/index"
import CandidateList from "../pages/JobPages/CandidateList"
import CandidateOverview from "../pages/JobPages/CandidateOverview"

// User Pages
import UserList from "../pages/UserPages/UserList"
import UserProfile from "pages/UserPages/UserProfile/user-profile"

// Brand Pages
import BrandList from "../pages/BrandPages/BrandList"

// Stock Pages
import StockList from "pages/StockPages/StockList"
import StockAddEdit from "pages/StockPages/StockList/StockAddEdit"
import StockView from "pages/StockPages/StockView"

// Other Pages
import ModelList from "../pages/ModelPages/ModelList"
import LocationList from "pages/LocationPages/LocationList"
import StatusList from "pages/StatusPages/StatusList"
import ColorList from "pages/ColorPages/ColorList"
import FeatureList from "pages/FeaturePages/FeatureList"
import PackageList from "pages/PackagePages/PackageList"
import YearList from "pages/YearPages/YearList"
import EngineCcList from "pages/EngineCcPages/EngineCcList"
import ChassisCodeList from "pages/ChassisCodePages/ChassisCodeList"
import EngineCodeList from "pages/EngineCodePages/EngineCodeList"
import FuelTypeList from "pages/FuelTypePages/FuelTypeList"
import EngineTypeList from "pages/EngineTypePages/EngineTypeList"
import KeyTypeList from "pages/KeyTypePages/KeyTypeList"
import AuctionGradeList from "pages/AuctionGradePages/AuctionGradeList"
import TransmissionList from "pages/TransmissionPages/TransmissionList"
import ShowroomList from "pages/ShowroomPages/ShowroomList"
import ShowroomProfile from "pages/ShowroomPages/ShowroomProfile"

// Contact Form
import MessageList from "pages/ContactFormPages/MessageList"

// Role & Permission
import ModuleList from "pages/RolePermissionPages/ModuleList"
import RoleList from "pages/RolePermissionPages/RoleList"
import PermissionList from "pages/RolePermissionPages/PermissionList"
import RolePermissionList from "pages/RolePermissionPages/RolePermissionList"

// Error Page (404)
import NotFound from "pages/ErrorPages/NotFound"

// Access Permission Constants
import { ACCESS } from "constants/accessPermissions"
import AccessDenied from "pages/ErrorPages/AccessDenied"
import VehicleImporterList from "pages/VehicleImporterPages/VehicleImporterList"
import VehicleImporterAddEdit from "pages/VehicleImporterPages/VehicleImporterList/VehicleImporterAddEdit"
import PostStockList from "pages/ContentManagerPages/PostStockList"
import MovementTypeList from "pages/MovementTypePages/MovementTypeList"
import MovementContainer from "pages/VehicleMovementPages/MovementContainer/MovementContainer"
import VehicleFuelStockList from "pages/VehicleFuelStockPages/VehicleFuelStockList/VehicleFuelStockList"
import VehicleFuelUsageList from "pages/VehicleFuelStockPages/VehicleFuelUsageList/VehicleFuelUsageList"
import CustomerDesignationList from "pages/CustomerPages/CustomerDesignationList/CustomerDesignationList"
import CustomerDepartmentList from "pages/CustomerPages/CustomerDepartment/CustomerDepartmentList"
import CustomerCompanyList from "pages/CustomerPages/CustomerCompany/CustomerCompanyList"
import CustomerAreaList from "pages/CustomerPages/CustomerArea/CustomerAreaList"
import CustomerCityList from "pages/CustomerPages/CustomerCity/CustomerCityList"
import CustomerTypeList from "pages/CustomerPages/CustomerType/CustomerTypeList"
import CustomerList from "pages/CustomerPages/Customer/CustomerList"
import CustomerProfile from "pages/CustomerPages/CustomerProfile"
import QuotationAddEdit from "pages/QuotationPages/QuotationAddEdit"
import AccessoriesList from "pages/AccessoriesPages/AccessoriesList/AccessoriesList"
import AccessoriesCompanyList from "pages/AccessoriesPages/AccessoriesCompany/AccessoriesCompanyList"
import AccessoriesStockList from "pages/AccessoriesPages/AccessoriesStock/AccessoriesStockList"
import UnitList from "pages/UnitPages/UnitList"
import AccessoriesUsageList from "pages/AccessoriesPages/AccessoriesUsage/AccessoriesUsagekList"
import ImporterView from "pages/VehicleImporterPages/ImporterView/ImporterView"

const authProtectedRoutes = [
  // 📊 Dashboard
  {
    path: "/dashboard",
    access: [ACCESS.DASHBOARD.VIEW_DASHBOARD],
    component: <Dashboard />,
  },
  {
    path: "/",
    exact: true,
    access: [ACCESS.DASHBOARD.VIEW_DASHBOARD],
    component: <Navigate to="/dashboard" />,
  },

  // 👤 User
  {
    path: "/user-list",
    access: [ACCESS.USER.VIEW_USER_LIST],
    component: <UserList />,
  },
  {
    path: "/user-list/:identifier/view",
    access: [ACCESS.USER.VIEW_PROFILE],
    component: <UserProfile />,
  },

  // 🚘 Brand
  {
    path: "/brand-list",
    access: [ACCESS.BRAND.VIEW_BRAND_LIST],
    component: <BrandList />,
  },

  // 📦 Model
  {
    path: "/model-list",
    access: [ACCESS.MODEL.VIEW_MODEL_LIST],
    component: <ModelList />,
  },

  // 📍 Location
  {
    path: "/location-list",
    access: [ACCESS.LOCATION.VIEW_LOCATION_LIST],
    component: <LocationList />,
  },

  // 📊 Status
  {
    path: "/status-list",
    access: [ACCESS.STATUS.VIEW_STATUS_LIST],
    component: <StatusList />,
  },

  // 🎨 Color
  {
    path: "/color-list",
    access: [ACCESS.COLOR.VIEW_COLOR_LIST],
    component: <ColorList />,
  },

  // ⚙️ Feature
  {
    path: "/feature-list",
    access: [ACCESS.FEATURE.VIEW_FEATURE_LIST],
    component: <FeatureList />,
  },

  // 📦 Package
  {
    path: "/package-list",
    access: [ACCESS.PACKAGE.VIEW_PACKAGE_LIST],
    component: <PackageList />,
  },

  // 📅 Year
  {
    path: "/year-list",
    access: [ACCESS.YEAR.VIEW_YEAR_LIST],
    component: <YearList />,
  },

  // 🧩 Engine CC
  {
    path: "/engine-cc-list",
    access: [ACCESS.ENGINE_CC.VIEW_ENGINE_CC_LIST],
    component: <EngineCcList />,
  },

  // 🔢 Chassis Code
  {
    path: "/chassis-code-list",
    access: [ACCESS.CHASSIS_CODE.VIEW_CHASSIS_CODE_LIST],
    component: <ChassisCodeList />,
  },

  // 🔧 Engine Code
  {
    path: "/engine-code-list",
    access: [ACCESS.ENGINE_CODE.VIEW_ENGINE_CODE_LIST],
    component: <EngineCodeList />,
  },

  // ⛽ Fuel Type
  {
    path: "/fuel-type-list",
    access: [ACCESS.FUEL_TYPE.VIEW_FUEL_TYPE_LIST],
    component: <FuelTypeList />,
  },

  // 🛠 Engine Type
  {
    path: "/engine-type-list",
    access: [ACCESS.ENGINE_TYPE.VIEW_ENGINE_TYPE_LIST],
    component: <EngineTypeList />,
  },

  // 🔑 Key Type
  {
    path: "/key-type-list",
    access: [ACCESS.KEY_TYPE.VIEW_KEY_TYPE_LIST],
    component: <KeyTypeList />,
  },

  // 🏁 Auction Grade
  {
    path: "/auction-grade-list",
    access: [ACCESS.AUCTION_GRADE.VIEW_AUCTION_GRADE_LIST],
    component: <AuctionGradeList />,
  },

  // ⚙️ Transmission
  {
    path: "/transmission-list",
    access: [ACCESS.TRANSMISSION.VIEW_TRANSMISSION_LIST],
    component: <TransmissionList />,
  },

  // 🏢 Showroom
  {
    path: "/showroom-list",
    access: [ACCESS.SHOWROOM.VIEW_SHOWROOM_LIST],
    component: <ShowroomList />,
  },
  {
    path: "/showroom-list/:slug/view",
    access: [ACCESS.SHOWROOM.VIEW_PROFILE],
    component: <ShowroomProfile />,
  },

  // 🚗 Stock
  {
    path: "/stock-list",
    access: [ACCESS.STOCK.VIEW_STOCK_LIST],
    component: <StockList />,
  },
  {
    path: "/stock-add-edit",
    access: [ACCESS.STOCK.CREATE],
    component: <StockAddEdit />,
  },
  {
    path: "/stock-add-edit/:id",
    access: [ACCESS.STOCK.EDIT],
    component: <StockAddEdit />,
  },
  {
    path: "/stock-list/:stockId/view",
    access: [ACCESS.STOCK.VIEW_DETAILS],
    component: <StockView />,
  },

  // 📨 Contact Message
  {
    path: "/message-list",
    access: [ACCESS.MESSAGE.VIEW_CONTACT_MESSAGE_LIST],
    component: <MessageList />,
  },

  // 📨 Movement Type
  {
    path: "/vehicle-movement-type-list",
    access: [ACCESS.VEHICLE_MOVEMENT_TYPE.VIEW_LIST],
    component: <MovementTypeList />,
  },

  // 📨 Vehicle Movement
  {
    path: "/vehicle-movement-list",
    access: [ACCESS.VEHICLE_MOVEMENT.VIEW_LIST],
    component: <MovementContainer />,
  },

  // Vehicle Movement Location
  {
    path: "/movement-location-list",
    access: [ACCESS.LOCATION.VIEW_LOCATION_LIST],
    component: <LocationList />,
  },

  // 🧩 Vehicle Fuel Usage
  {
    path: "/fuel-usage-list",
    access: [ACCESS.VEHICLE_FUEL_USE.VIEW_LIST],
    component: <VehicleFuelUsageList />,
  },
  // 🧩 Vehicle Fuel Stock
  {
    path: "/purchased-fuel-list",
    access: [ACCESS.VEHICLE_FUEL_STOCK.VIEW_LIST],
    component: <VehicleFuelStockList />,
  },
  // 🧩 Fuel Station
  {
    path: "/fuel-station-list",
    access: [ACCESS.LOCATION.VIEW_LOCATION_LIST],
    component: <LocationList />,
  },

  // 🧩 Module
  {
    path: "/module-list",
    access: [ACCESS.MODULE.VIEW_MODULE_LIST],
    component: <ModuleList />,
  },

  // 🔐 Role
  {
    path: "/role-list",
    access: [ACCESS.ROLE.VIEW_ROLE_LIST],
    component: <RoleList />,
  },

  // 🔑 Permission
  {
    path: "/permission-list",
    access: [ACCESS.PERMISSION.VIEW_PERMISSION_LIST],
    component: <PermissionList />,
  },

  // 🧩 Role Permission Assignment
  {
    path: "/role/:roleSlug/permission-list",
    access: [ACCESS.ROLE_PERMISSION.ASSIGN_ROLE_PERMISSIONS],
    component: <RolePermissionList />,
  },

  // 🚘 Vehicle Importer
  {
    path: "/importer-list",
    access: [ACCESS.VEHICLE_IMPORTER.VIEW_VEHICLE_IMPORTER_LIST],
    component: <VehicleImporterList />,
  },
  {
    path: "/importer-list/:identifier/view",
    access: [ACCESS.VEHICLE_IMPORTER.VIEW_VEHICLE_IMPORTER],
    component: <ImporterView />,
  },
  {
    path: "/importer/add",
    access: [ACCESS.VEHICLE_IMPORTER.CREATE_VEHICLE_IMPORTER],
    component: <VehicleImporterAddEdit />,
  },
  {
    path: "/importer/:importerId/edit",
    access: [ACCESS.VEHICLE_IMPORTER.EDIT_VEHICLE_IMPORTER],
    component: <VehicleImporterAddEdit />,
  },
  {
    path: "/importers/:importerId/details",
    access: [ACCESS.VEHICLE_IMPORTER.VIEW_VEHICLE_IMPORTER],
    component: <VehicleImporterList />,
  },

  // 🧠 Content Manager
  {
    path: "/vehicle-content-list",
    access: [ACCESS.CONTENT_MANAGER.VIEW_VEHICLE_CONTENT_LIST],
    component: <PostStockList />,
  },

  // 💼 Job
  {
    path: "/job-grid",
    access: [ACCESS.JOB.VIEW_GRID],
    component: <JobGrid />,
  },
  {
    path: "/job-details",
    access: [ACCESS.JOB.VIEW_DETAILS],
    component: <JobDetails />,
  },
  {
    path: "/job-categories",
    access: [ACCESS.JOB.VIEW_CATEGORIES],
    component: <JobCategories />,
  },
  {
    path: "/job-list",
    access: [ACCESS.JOB.VIEW_GRID],
    component: <JobList />,
  },
  {
    path: "/job-apply",
    access: [ACCESS.JOB.APPLY],
    component: <ApplyJobs />,
  },
  {
    path: "/candidate-list",
    access: [ACCESS.JOB.VIEW_CANDIDATES],
    component: <CandidateList />,
  },
  {
    path: "/candidate-overview",
    access: [ACCESS.JOB.VIEW_CANDIDATE_DETAILS],
    component: <CandidateOverview />,
  },

  /**
  --------------
  -- Customer --
  --------------
  */

  // 🔑 Customer
  {
    path: "/customer-list",
    access: [ACCESS.CUSTOMER.VIEW_LIST],
    component: <CustomerList />,
  },
  // Customer Profile
  {
    path: "/customer-list/:identifier/view",
    access: [ACCESS.CUSTOMER.VIEW_LIST],
    component: <CustomerProfile />,
  },
  // Customer Designation
  {
    path: "/customer-designation-list",
    access: [ACCESS.CUSTOMER_DESIGNATION.VIEW_LIST],
    component: <CustomerDesignationList />,
  },
  // Customer Department List
  {
    path: "/customer-department-list",
    access: [ACCESS.CUSTOMER_DEPARTMENT.VIEW_LIST],
    component: <CustomerDepartmentList />,
  },
  // Customer Company List
  {
    path: "/customer-Company-list",
    access: [ACCESS.CUSTOMER_COMPANY.VIEW_LIST],
    component: <CustomerCompanyList />,
  },
  // Customer Area List
  {
    path: "/customer-area-list",
    access: [ACCESS.CUSTOMER_AREA.VIEW_LIST],
    component: <CustomerAreaList />,
  },
  // Customer City List
  {
    path: "/customer-city-list",
    access: [ACCESS.CUSTOMER_CITY.VIEW_LIST],
    component: <CustomerCityList />,
  },
  // Customer Type List
  {
    path: "/customer-type-list",
    access: [ACCESS.CUSTOMER_TYPE.VIEW_LIST],
    component: <CustomerTypeList />,
  },
  // Quotation Add Page
  {
    path: "/quotation-list/add",
    access: [ACCESS.CUSTOMER_TYPE.VIEW_LIST],
    component: <QuotationAddEdit />,
  },
  // Quotation Edit Page
  {
    path: "/quotation-list/:identifier/add",
    access: [ACCESS.CUSTOMER_TYPE.VIEW_LIST],
    component: <QuotationAddEdit />,
  },

  /**
  -------------------------------------
  -- Accessories Pages --
  -------------------------------------
   */
  {
    path: "/accessories-list",
    access: [ACCESS.ACCESSORIES.VIEW_LIST],
    component: <AccessoriesList />,
  },
  {
    path: "/accessories-store-list",
    access: [ACCESS.ACCESSORIES_COMPANY.VIEW_LIST],
    component: <AccessoriesCompanyList />,
  },
  {
    path: "/accessories-stock-list",
    access: [ACCESS.ACCESSORIES_STOCK.VIEW_LIST],
    component: <AccessoriesStockList />,
  },
  {
    path: "/accessories-usage-list",
    access: [ACCESS.ACCESSORIES_USAGE.VIEW_LIST],
    component: <AccessoriesUsageList />,
  },
  /**
  ------------------------------
  -- Unit Pages --
  ------------------------------
  */
  {
    path: "/accessories-unit-list",
    access: [ACCESS.UNIT.VIEW_LIST],
    component: <UnitList />,
  },

  // ❌ 401 fallback
  {
    path: "401",
    component: <AccessDenied />,
  },
  // ❌ 404 fallback
  {
    path: "*",
    component: <NotFound />,
  },
]

const publicRoutes = [
  { path: "/login", component: <Login /> },
  { path: "/logout", component: <Logout /> },
  { path: "/forgot-password", component: <ForgetPwd /> },
  { path: "/register", component: <Register /> },
  { path: "*", component: <NotFound /> }, // 404 for public pages
]

export { authProtectedRoutes, publicRoutes }
